/**
 * What to call one connection when several exist for the same connector.
 *
 * The connections table showed the first 8 characters of the connection id
 * under the connector name to tell such rows apart. That works for a Postgres
 * row (a bare uuid) and not at all for a UCL one, whose id is
 * `ucl:<workspace>:<endOrg>:<connector>:<identity>[:<name>]` — every row in a
 * workspace shares that prefix, so two Intercom connections both read
 * "ucl:pers". The connection's own name ("default", "connection-1", or whatever
 * the person typed) is on the wire from both backends and is the thing that is
 * actually distinct.
 */

/** The sequence the platform generates for an unnamed connect — services/api's
 *  nextUclConnectionName and its Postgres twin. Only these are title-cased; a
 *  name a person chose is shown exactly as they typed it. */
const GENERATED_NAME = /^(default|connection-\d+)$/;

/** The most distinguishing part of an id, for a row whose name is missing. */
function shortConnectionId(id: string): string {
  if (id.startsWith("ucl:")) {
    const last = id.split(":").pop() ?? id;
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  }
  return id.slice(0, 8);
}

export function connectionDisplayName(name: string | null | undefined, id: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return shortConnectionId(id);
  return GENERATED_NAME.test(trimmed)
    ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    : trimmed;
}

/**
 * What a rename field starts with: the RAW stored name.
 *
 * Deliberately NOT connectionDisplayName(). That title-cases a generated name
 * and the row appends connectionAddressSuffix() after it, so the label reads
 * "Default - made in the partner workspace" for a connection whose name is the
 * four letters "default". Seeding the field from the label and committing it
 * would store the annotation as the name.
 */
export function renameSeed(name: string | null | undefined): string {
  return (name ?? "").trim();
}

/**
 * Whether a committed draft is worth sending.
 *
 * Save is disabled until this is true, so the dialog cannot send a PUT that
 * renames a connection to what it is already called, or one that clears the
 * name — the platform would just re-derive a generated one.
 */
export function renameChanges(draft: string, current: string | null | undefined): boolean {
  const next = draft.trim();
  return next !== "" && next !== renameSeed(current);
}

/**
 * Which of several connections to the same connector a row is: "1 of 2", "2 of 2", oldest first.
 *
 * Two HubSpot rows read "Default" and "Connection-1", which does not say they are two connections
 * to one app, and "Default" reads like the default setting. The names stay (the API and the agent
 * use them), and rows that share a connector — and a customer, in the customers view — get their
 * place among each other. Only groups of two or more are numbered; a lone connection needs none.
 * Counted over the rows given; pass the unfiltered list so a row keeps its number under a filter.
 */
export function connectionOrdinals<T extends { id: string; createdAt?: string | null }>(
  rows: readonly T[],
  group: (row: T) => string,
): Map<string, { n: number; of: number }> {
  const groups = new Map<string, T[]>();
  for (const r of rows) {
    const key = group(r);
    const list = groups.get(key);
    if (list) list.push(r);
    else groups.set(key, [r]);
  }
  const out = new Map<string, { n: number; of: number }>();
  for (const list of groups.values()) {
    if (list.length < 2) continue;
    const ordered = list
      .map((r, i) => ({ r, i, t: r.createdAt ? Date.parse(r.createdAt) : Number.NaN }))
      .sort((a, b) => {
        const at = Number.isNaN(a.t) ? Infinity : a.t;
        const bt = Number.isNaN(b.t) ? Infinity : b.t;
        return at !== bt ? at - bt : a.i - b.i;
      });
    ordered.forEach(({ r }, k) => out.set(r.id, { n: k + 1, of: list.length }));
  }
  return out;
}

/**
 * A listed connection can sit at either of the TWO UCL credential addresses a tenant's
 * connections accumulate at (ADR-021): the partner's workspace with the tenant as the segment
 * (written by the widget, an impersonating admin, a setup link) or the tenant's own workspace
 * (written by a direct login into it). The api flags the rows that came from the address the
 * request did NOT resolve to.
 *
 * `connectionDisplayName` above cannot tell them apart and should not try: names are generated
 * PER ADDRESS, so the same provider account connected on both surfaces is genuinely called
 * "default" at each. The name is not wrong, it is just not unique - which is why this is a
 * separate axis rather than a change to that function.
 *
 * Two things are only true of a connection at the caller's OWN address:
 *
 *  - it can hold the DEFAULT. UCL keeps that as a pointer per (workspace, endOrg) whose target
 *    carries no workspace, so it cannot name a scope at the other one; the api answers 409.
 *  - its display name is not necessarily unique, per above - so a picker must label the other
 *    one and must select by id.
 *
 * Read structurally because `ConnectionItem` comes from @fastn-ai-team/connector-ui, whose type
 * does not carry the field yet; the value is passed through verbatim at runtime.
 */
export const atAlternateAddress = (connection: unknown): boolean =>
  (connection as { atAlternateAddress?: boolean } | null | undefined)?.atAlternateAddress === true;

/**
 * WHERE a flagged row actually lives, in words - and it is NOT one fixed sentence, which is the
 * mistake the first version of this made.
 *
 * `atAlternateAddress` means "not the address this request resolved to", and which address that is
 * depends on the surface. From a tenant's own dashboard login the alternate is the PARTNER's
 * workspace. From the widget - or a partner previewing as that customer - the primary IS the
 * partner's workspace, so the alternate is the customer's own. A single "made in the partner
 * workspace" string is therefore correct on one surface and backwards on the other.
 *
 * The direction is derivable from fields already on the wire, so this needs no new field: a
 * DELEGATED row carries the partner as `orgId` with the tenant in `endOrgId`, and a row at an
 * org's OWN workspace carries no segment (or its own org as the segment - the ConnectModal's
 * self-stamp). Nothing here guesses from the caller, which is what makes it right on both.
 */
export function connectionAddressLabel(connection: unknown): string | null {
  if (!atAlternateAddress(connection)) return null;
  const row = connection as { orgId?: string; endOrgId?: string | null };
  const delegated = !!row.endOrgId && row.endOrgId !== row.orgId;
  return delegated ? "Made in the partner organization" : "Made in the customer's own organization";
}

/** The same thing as a suffix for an option label, or "" for a row at the caller's own address. */
export function connectionAddressSuffix(connection: unknown): string {
  const label = connectionAddressLabel(connection);
  return label ? ` - ${label.replace(/^Made /, "made ")}` : "";
}

/**
 * Is this key event the input method's, rather than the user's?
 *
 * `isComposing` is the documented answer, but Safari fires `compositionend`
 * BEFORE `keydown`, so the Enter or Escape that commits a candidate arrives
 * with `isComposing: false`. What it does carry is the legacy `keyCode 229`,
 * the "processing" sentinel every engine uses while an IME owns the keystroke.
 * Checking only `isComposing` means Safari still saves a half-composed name on
 * Enter, and still throws the draft away on Escape.
 */
export function fromInputMethod(e: { isComposing?: boolean; keyCode?: number }): boolean {
  return e.isComposing === true || e.keyCode === 229;
}
